import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, CheckCircle2, Clock, Shield, Droplets, ArrowRight } from 'lucide-react';
import WorkGallery from '../../components/WorkGallery';

const CeramicCoating = () => {
  const workImages = [
    '/siteimages/image_0001.jpg',
    '/siteimages/image_0025.jpg',
    '/siteimages/image_0028.jpg',
    '/siteimages/image_0032.jpg',
  ];

  return (
    <div>
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Ceramic Coating
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Premium ceramic coating services for long-lasting paint protection and showroom shine.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Advanced <span className="text-bmw-blue">Paint Protection</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Ceramic coating provides a permanent or semi-permanent bond with your vehicle's paint, creating 
                a protective layer that resists UV rays, chemicals, dirt, and minor scratches. Unlike traditional 
                waxes, ceramic coatings last for years and maintain their protective properties.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our professional-grade ceramic coating creates an incredibly smooth, hydrophobic surface that 
                repels water and contaminants. This makes your vehicle easier to clean while maintaining that 
                fresh, showroom shine for years to come.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-bmw-blue" />
                  Long-Lasting Protection
                </h3>
                <p className="text-gray-600">
                  Our ceramic coatings provide protection for multiple years, far exceeding traditional waxes and sealants.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">Coating Benefits</h3>
                <ul className="space-y-3">
                  {[
                    'UV ray and oxidation protection',
                    'Chemical and acid rain resistance',
                    'Hydrophobic water repelling',
                    'Enhanced gloss and depth',
                    'Easier cleaning and maintenance',
                    'Minor scratch resistance',
                    'Long-lasting durability',
                    'Increased resale value',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Protect Your Investment</h3>
                <p className="text-gray-600 mb-4">
                  Get a quote for professional ceramic coating application and protect your vehicle for years.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Years of Protection</h3>
              <p className="text-soft-grey">
                Professional ceramic coatings last 3-5 years with proper maintenance.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Products</h3>
              <p className="text-soft-grey">
                We use only professional-grade ceramic coatings for superior results.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Showroom Finish</h3>
              <p className="text-soft-grey">
                Incredible gloss and depth that enhances your vehicle's appearance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Ceramic Coating Results" />
        </div>
      </section>

      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready for Ceramic Coating?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Invest in long-term paint protection with professional ceramic coating. Contact us for a consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CeramicCoating;
